import type { Animation } from '../types';

const DAY_MS = 24 * 60 * 60 * 1000;
/** 新着度の時定数（日）。exp(-経過日数/30) はおよそ経過30日で0.37倍になる。 */
const RECENCY_TAU_DAYS = 30;

/**
 * おすすめ順のスコア。score = likes * exp(-経過日数/30)。
 * 暫定ロジック。将来バックエンドのレコメンド API に置き換える想定。
 */
function recommendScore(a: Animation, now: number): number {
  const elapsedDays = Math.max(0, (now - new Date(a.createdAt).getTime()) / DAY_MS);
  return a.likes * Math.exp(-elapsedDays / RECENCY_TAU_DAYS);
}

/** おすすめ順（スコア降順）に並べ替えた新しい配列を返す。元の配列は変更しない。 */
export function sortByRecommended(items: Animation[]): Animation[] {
  const now = Date.now();
  return [...items].sort((a, b) => recommendScore(b, now) - recommendScore(a, now));
}

/** タグ Jaccard 係数（0〜1）。片方でもタグがあれば算出し、両方タグ無し（0/0）は情報無しとして 0 を返す。 */
function tagJaccard(a: string[], b: string[]): number {
  const setA = new Set(a);
  const setB = new Set(b);
  if (setA.size === 0 && setB.size === 0) return 0;
  let intersection = 0;
  for (const t of setA) if (setB.has(t)) intersection++;
  const union = setA.size + setB.size - intersection;
  return union > 0 ? intersection / union : 0;
}

// タグの一致を主（shortsRecommend.ts の作者0.35寄りの慣行を踏襲しつつ、詳細ページでは
// カテゴリ一致も判断材料として使えるため category / owner に分けて配分する）。
const RELATED_WEIGHT_TAG = 0.65;
const RELATED_WEIGHT_CATEGORY = 0.2;
const RELATED_WEIGHT_OWNER = 0.15;

/** target との親和性スコア（0〜1）。タグの Jaccard 係数を主軸に、カテゴリ・作者(ownerId)の一致を加点する。 */
function relatedAffinity(target: Animation, a: Animation): number {
  const tagScore = tagJaccard(target.tags, a.tags);
  const categoryScore = target.category && a.category && target.category === a.category ? 1 : 0;
  // ownerId は認証導入前のアイテムには無いため、両方に値がある場合のみ加点する。
  const ownerScore = target.ownerId && a.ownerId && target.ownerId === a.ownerId ? 1 : 0;
  return (
    RELATED_WEIGHT_TAG * tagScore +
    RELATED_WEIGHT_CATEGORY * categoryScore +
    RELATED_WEIGHT_OWNER * ownerScore
  );
}

/**
 * target（詳細ページで表示中のアニメーション）に関連するアイテムを pool から抽出し、
 * 親和性が高い順に並べ替えて返す（詳細ページの「関連ポーズ」セクション用）。
 *
 * 除外対象: target 自身、非公開(private)、センシティブ（ただし target 自身がセンシティブな
 * 場合は、閲覧中と同種の表現をブロックしないようセンシティブなアイテムも候補に含める）。
 *
 * 並び順: 親和性スコア（タグ Jaccard 主軸 + category / ownerId 一致の加点）降順。
 * 同点のタイブレークは sortByRecommended と同じ人気×新着スコア（likes * exp(-経過日数/30)）の
 * 降順にする。親和性が 0 のアイテムもグリッドが空になりにくいよう除外せず末尾に含める。
 */
export function relatedTo(target: Animation, pool: Animation[]): Animation[] {
  const now = Date.now();
  const targetIsSensitive = target.rating === 'sensitive';

  const candidates = pool.filter((a) => {
    if (a.id === target.id) return false;
    if ((a.visibility ?? 'public') === 'private') return false;
    if (a.rating === 'sensitive' && !targetIsSensitive) return false;
    return true;
  });

  return candidates
    .map((a) => ({ a, affinity: relatedAffinity(target, a), popularity: recommendScore(a, now) }))
    .sort((x, y) => {
      if (x.affinity !== y.affinity) return y.affinity - x.affinity;
      return y.popularity - x.popularity;
    })
    .map(({ a }) => a);
}
